import { Fragment, type CSSProperties, type ReactNode } from 'react';
import type { EstimateDocRichText } from '@notifi/shared-types/estimate-doc-template';

/**
 * A minimal renderer for the authored prose blocks (`authText`/`ackText`/
 * `richText`), whose content is TipTap/ProseMirror document JSON. This is a
 * deliberately small walk over the node shapes those editors emit — paragraphs,
 * headings, lists, text runs with basic marks and hard breaks — not a full
 * ProseMirror schema. Unknown nodes render their text/children so nothing is
 * silently dropped, and a plain string is treated as a single paragraph.
 *
 * PR3a scope: enough to lay authored copy into the document. The builder's live
 * editor and richer node types arrive with the template builder UI.
 */

interface ProseNode {
  type?: string;
  text?: string;
  content?: ProseNode[];
  marks?: { type?: string }[];
  attrs?: { level?: number };
}

function applyMarks(text: string, marks: ProseNode['marks'], keyBase: string): ReactNode {
  if (!marks || marks.length === 0) return text;
  return marks.reduce<ReactNode>((node, mark, i) => {
    const key = `${keyBase}-m${i}`;
    switch (mark.type) {
      case 'bold':
      case 'strong':
        return <strong key={key}>{node}</strong>;
      case 'italic':
      case 'em':
        return <em key={key}>{node}</em>;
      case 'underline':
        return <u key={key}>{node}</u>;
      case 'strike':
        return <s key={key}>{node}</s>;
      default:
        return node;
    }
  }, text);
}

function renderNodes(nodes: ProseNode[] | undefined, keyBase: string): ReactNode {
  if (!nodes || nodes.length === 0) return null;
  return nodes.map((node, i) => (
    <Fragment key={`${keyBase}-${i}`}>{renderNode(node, `${keyBase}-${i}`)}</Fragment>
  ));
}

function renderNode(node: ProseNode, key: string): ReactNode {
  switch (node.type) {
    case 'text':
      return applyMarks(node.text ?? '', node.marks, key);
    case 'hardBreak':
      return <br />;
    case 'paragraph':
      return <p className="mb-2 last:mb-0">{renderNodes(node.content, key)}</p>;
    case 'heading': {
      const level = Math.min(Math.max(node.attrs?.level ?? 2, 1), 6);
      const Tag = (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const)[level - 1];
      return (
        <Tag className="mb-2 font-semibold" style={{ fontFamily: 'var(--doc-heading-font)' }}>
          {renderNodes(node.content, key)}
        </Tag>
      );
    }
    case 'bulletList':
      return <ul className="mb-2 list-disc pl-5">{renderNodes(node.content, key)}</ul>;
    case 'orderedList':
      return <ol className="mb-2 list-decimal pl-5">{renderNodes(node.content, key)}</ol>;
    case 'listItem':
      return <li>{renderNodes(node.content, key)}</li>;
    default:
      // Unknown/container node: fall through to its children so no copy is lost.
      return node.content ? renderNodes(node.content, key) : (node.text ?? null);
  }
}

export function DocRichText({
  content,
  style,
  className,
}: {
  content?: EstimateDocRichText | string | null;
  style?: CSSProperties;
  className?: string;
}) {
  if (!content) return null;

  if (typeof content === 'string') {
    const text = content.trim();
    if (!text) return null;
    return (
      <div className={className} style={style}>
        <p className="whitespace-pre-wrap">{text}</p>
      </div>
    );
  }

  const body = renderNodes(content.content as ProseNode[] | undefined, 'root');
  if (!body) return null;
  return (
    <div className={className} style={style}>
      {body}
    </div>
  );
}
